'use client';

import { useEffect } from 'react';

/** Any event target the hook can attach to -- document, window, an element -- or null for SSR. `EventTarget` rather
 *  than a closed list so the window-or-null probe `documentTarget()` (`const/shared-window-state.ts`) binds directly. */
type ListenerTarget = EventTarget | null;

interface UseEventListenerOptions {
	/** The event target (document, window, an element, or null for SSR) */
	target: ListenerTarget;
	/** Whether the listener is active (default: true) */
	enabled?: boolean;
}

/** Adds/removes a single event listener with cleanup. `handler` must be stable (memoized); `options` carries the target and an `enabled` flag. */
export function useEventListener(event: string, handler: (event: Event) => void, options: UseEventListenerOptions): void {
	const { target, enabled = true } = options;
	useEffect(() => {
		if (!enabled || !target) return;

		target.addEventListener(event, handler);
		return () => target.removeEventListener(event, handler);
	}, [target, event, enabled, handler]);
}
